/**
 * Card language resolution for TTS and repair.
 * Resolves effective language/locale with priority: explicit field > conflict override > deck > detected > fallback.
 */
import type { DeckLang } from "@/types/linguadeck";
import { detectLanguageFromText } from "./detectLanguage";
import { resolveBestLocale } from "./tts/languageMapping";

const STUDY_LANG_CODES = new Set<string>([
  "en", "es", "fr", "de", "it", "nl", "pl", "pt-BR", "tr", "ko", "ru", "uk",
  "en-US", "en-GB", "es-ES", "es-MX", "uk-UA", "ru-RU", "be", "be-BY",
]);

function isValidLangCode(code: string): boolean {
  const base = (code ?? "").toString().trim();
  if (!base) return false;
  const simple = base.split("-")[0];
  return STUDY_LANG_CODES.has(base) || STUDY_LANG_CODES.has(simple);
}

/** Conflict: Cyrillic text detected as Ukrainian but stored as Russian (common Quizlet import bug). */
function hasUkRuConflict(
  text: string,
  storedLang: string,
  detectedLang: DeckLang | null
): boolean {
  const stored = (storedLang ?? "").toLowerCase();
  if (stored !== "ru" && stored !== "ru-ru") return false;
  return detectedLang === "uk";
}

export interface ResolveEffectiveOptions {
  card: { front?: string; back?: string; frontLang?: string | null; backLang?: string | null };
  side: "front" | "back";
  deckLang?: string;
  fallback?: string;
}

/**
 * Resolves effective language code for TTS. Priority:
 * 1. Explicit frontLang/backLang if valid
 * 2. Conflict override: Cyrillic text detected as uk, stored ru → use uk
 * 3. deckLang
 * 4. Detected from text
 * 5. fallback (default "en")
 */
export function resolveEffectiveCardSideLanguage(options: ResolveEffectiveOptions): string {
  const { card, side, deckLang, fallback = "en" } = options;
  const text = (side === "front" ? card.front : card.back) ?? "";
  const stored = (side === "front" ? card.frontLang : card.backLang) ?? "";
  const storedTrimmed = (typeof stored === "string" ? stored : String(stored)).trim();
  const deck = (deckLang ?? "").trim();

  const detected = detectLanguageFromText(text);

  if (storedTrimmed && isValidLangCode(storedTrimmed)) {
    if (hasUkRuConflict(text, storedTrimmed, detected)) {
      const resolved = detected === "uk" ? "uk" : storedTrimmed;
      if (typeof console !== "undefined" && console.info) {
        console.info("[card_language_resolved] conflict_override_uk_ru", {
          side,
          stored: storedTrimmed,
          detected,
        });
        console.info("[card_language] card_speech_locale_resolved", { side, lang: resolved, source: "override" });
      }
      return resolved;
    }
    return storedTrimmed;
  }

  if (deck && isValidLangCode(deck)) {
    if (hasUkRuConflict(text, deck, detected)) {
      const resolved = detected === "uk" ? "uk" : deck;
      if (typeof console !== "undefined" && console.info) {
        console.info("[card_language_resolved] conflict_override_deck_uk_ru", { side, deck, detected });
        console.info("[card_language] card_speech_locale_resolved", { side, lang: resolved, source: "override" });
      }
      return resolved;
    }
    return deck;
  }

  if (detected && isValidLangCode(detected)) {
    if (typeof console !== "undefined" && console.info) {
      console.info("[card_language_resolved] detected_from_text", { side, detected });
      console.info("[card_language] card_speech_locale_resolved", { side, lang: detected, source: "detected" });
    }
    return detected;
  }

  return fallback;
}

/**
 * Resolves effective BCP-47 speech locale for TTS.
 */
export function resolveEffectiveSpeechLocale(options: ResolveEffectiveOptions): string {
  const lang = resolveEffectiveCardSideLanguage(options);
  const locale = resolveBestLocale(lang, options.fallback ?? "en-US");
  return locale;
}
